import { db } from '@backend/db/db'
import { ChevronLeftIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { ActionButton, SolidActionButton } from '@components/Buttons'
import { useAuth } from '@hb/react'
import {
  Encounter,
  EncounterStatus,
  EncounterType,
  getDateString,
  getPatientStatementsCollectionPath,
  PatientStatement,
  PrenatalExamData,
  TelephoneContact,
  WithId,
} from '@hb/shared'
import { useQuery } from '@hooks/backend/useQuery'
import { getLocalStorage, setLocalStorage } from '@utils/localStorage'
import { collection, CollectionReference, query, where } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { useProfile } from '../../../../contexts'
import {
  useAssessmentContacts,
  useAssessmentExams,
  useAssessmentProviderFlags,
  usePatientThreadMessages,
} from '../../../../hooks/backend'
import { EncounterForm } from '../../../Encounters/EncounterForm'
import { EncounterView } from '../../../Encounters/EncounterView'
import { useEncounterOpenState } from '../../../Encounters/useEncounterOpenState'
import { LogContactModal } from '../LogContactModal'
import { FlowSheetGrid } from './FlowSheetGrid'
import { FlowSheetTimeline } from './FlowSheetTimeline'
import { PdfExportModal } from './PdfExportModal'
import { QuickNoteModal } from './QuickNoteModal'
import { formatBp, formatGa } from './utils'

type FlowSheetView = 'grid' | 'timeline'

const toDateStr = (ms: number) => new Date(ms).toISOString().slice(0, 10)

const statusColor: Record<string, string> = {
  [EncounterStatus.DRAFT]: 'yellow',
  [EncounterStatus.COMPLETED]: 'blue',
  [EncounterStatus.AUTHENTICATED]: 'green',
}

const LastVisitCard = ({
  encounter,
  onOpenEncounter,
}: {
  encounter: WithId<Encounter>
  onOpenEncounter: (id: string | null) => void
}) => {
  const data = encounter.data as PrenatalExamData
  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800' }}
      borderRadius={8}
      border="1px solid"
      borderColor="gray.200"
      boxShadow="0 1px 4px #00000018"
      p={4}>
      <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
        <VStack align="flex-start" spacing={1}>
          <HStack>
            <Text fontFamily="Hero-New" fontWeight={700} fontSize="sm">
              Last Visit: {encounter.date ? getDateString(encounter.date, 'short', true) : '—'}
            </Text>
            {data.gestationalAge != null && (
              <Badge colorScheme="blue" fontFamily="Hero-New" fontSize="xs">
                {formatGa(data.gestationalAge)}
              </Badge>
            )}
            <Badge
              colorScheme={statusColor[encounter.status] ?? 'gray'}
              fontFamily="Hero-New"
              fontSize="xs">
              {encounter.status}
            </Badge>
          </HStack>
          <HStack spacing={3} flexWrap="wrap">
            {encounter.renderingProviderInitials && (
              <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
                Provider: {encounter.renderingProviderInitials}
              </Text>
            )}
            {encounter.data.vital?.bloodPressure && (
              <Text fontSize="xs" color="gray.600" fontFamily="Hero-New">
                BP: {formatBp(encounter.data.vital.bloodPressure)}
              </Text>
            )}
            {encounter.data.vital?.weight != null && (
              <Text fontSize="xs" color="gray.600" fontFamily="Hero-New">
                Wt: {encounter.data.vital.weight} lbs
              </Text>
            )}
            {data.fetalHeartTones != null && (
              <Text fontSize="xs" color="gray.600" fontFamily="Hero-New">
                FHT: {data.fetalHeartTones} bpm
              </Text>
            )}
          </HStack>
        </VStack>
        <ButtonGroup size="sm" spacing={2}>
          {/* "Open Note", not "Open Full Note" — a Saved note opens on the quick-auth
              summary first (§1.6 Quick Authenticate), so this label must not overpromise. */}
          <ActionButton fontFamily="Hero-New" onClick={() => onOpenEncounter(encounter.id)}>
            Open Note
          </ActionButton>
          <SolidActionButton fontFamily="Hero-New" onClick={() => onOpenEncounter(null)}>
            Start New Visit
          </SolidActionButton>
        </ButtonGroup>
      </Flex>
    </Box>
  )
}

interface Props {
  assessmentId: string
  onOpenLabQuickview?: () => void
  defaultView?: FlowSheetView
  onClose?: () => void
}

export const PrenatalFlowSheet = ({
  assessmentId,
  onOpenLabQuickview,
  defaultView,
  onClose,
}: Props) => {
  const { userId: patientId, patientRef, selectedAssessment } = useProfile()
  const assessment = selectedAssessment.populated
  const { dataArr, loading } = useAssessmentExams(assessmentId, EncounterType.ANTEPARTUM)
  const { dataArr: ultrasoundArr } = useAssessmentExams(assessmentId, EncounterType.ULTRASOUND)
  const { dataArr: contactsArr } = useAssessmentContacts(patientId, assessmentId)
  const { dataArr: flagsArr } = useAssessmentProviderFlags(patientRef, assessmentId)
  const startDateStr = useMemo(
    () => (assessment?.createdOn ? toDateStr(assessment.createdOn) : null),
    [assessment?.createdOn],
  )
  const endDateStr = useMemo(
    () => toDateStr(assessment?.deliveredOn ?? Date.now()),
    [assessment?.deliveredOn],
  )
  const portalMessages = usePatientThreadMessages(patientId, startDateStr, endDateStr)
  const statementsRef = useMemo(() => {
    if (!assessmentId) return null
    if (!patientId) return null
    return query(
      collection(
        db,
        getPatientStatementsCollectionPath(patientId),
      ) as CollectionReference<PatientStatement>,
      where('assessmentId', '==', assessmentId),
    )
  }, [assessmentId, patientId])
  const { dataArr: patientStatements } = useQuery<PatientStatement>(statementsRef)
  const [view, setView] = useState<FlowSheetView>(defaultView ?? 'grid')
  // §1.6 / M.2: status-driven routing between the read-only view and the form.
  const {
    selection: openEncounter,
    surface,
    open: setOpenEncounter,
    onEdit,
    onSaveSuccess,
    amendmentReason,
    onAmendmentReasonConsumed,
  } = useEncounterOpenState()
  const [editingContact, setEditingContact] = useState<WithId<TelephoneContact> | null>(null)
  const userId = useAuth(s => s.authUser?.uid)
  const flowSheetShowMoreKey = userId ? `hb_flowSheet_showMore_${userId}` : undefined
  const [showMore, setShowMoreState] = useState<boolean>(() =>
    flowSheetShowMoreKey
      ? ((getLocalStorage(flowSheetShowMoreKey) as boolean | null) ?? false)
      : false,
  )
  const setShowMore = (val: boolean) => {
    setShowMoreState(val)
    if (flowSheetShowMoreKey) setLocalStorage(flowSheetShowMoreKey, val)
  }
  const {
    isOpen: isQuickNoteOpen,
    onOpen: openQuickNote,
    onClose: closeQuickNote,
  } = useDisclosure()
  const { isOpen: isPdfOpen, onOpen: openPdf, onClose: closePdf } = useDisclosure()

  const encounters: WithId<Encounter>[] = useMemo(() => dataArr ?? [], [dataArr])
  const ultrasoundEncounters: WithId<Encounter>[] = useMemo(
    () => ultrasoundArr ?? [],
    [ultrasoundArr],
  )
  const contacts: WithId<TelephoneContact>[] = useMemo(() => contactsArr ?? [], [contactsArr])
  const flags = useMemo(() => flagsArr ?? [], [flagsArr])
  // Timeline view is chronological (ascending); grid view is most-recent-first (already sorted by useAssessmentExams desc)
  const timelineEncounters = useMemo(() => [...encounters].reverse(), [encounters])

  const handleOpenEncounter = useCallback(
    (id: string | null) => {
      const status = id
        ? [...encounters, ...ultrasoundEncounters].find(e => e.id === id)?.status
        : undefined
      setOpenEncounter({ type: EncounterType.ANTEPARTUM, examId: id, config: null, status })
    },
    [setOpenEncounter, encounters, ultrasoundEncounters],
  )

  const handleEditContact = useCallback((contact: WithId<TelephoneContact>) => {
    setEditingContact(contact)
  }, [])

  const handleCloseEditContact = useCallback(() => {
    setEditingContact(null)
  }, [])

  return (
    <Box position="relative" h="100%" display="flex" flexDir="column">
      {/* ── Header ── */}
      <Flex
        align="center"
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="gray.50"
        _dark={{ borderColor: 'whiteAlpha.200', bg: 'gray.800' }}
        gap={3}
        flexWrap="wrap"
        flexShrink={0}>
        <Text
          fontFamily="Hero-New"
          fontWeight={700}
          fontSize="md"
          color="gray.700"
          _dark={{ color: 'gray.200' }}>
          {openEncounter || view === 'grid' ? 'Prenatal Flow Sheet' : 'Episode Timeline'}
        </Text>
        {openEncounter ? (
          <ActionButton
            ml="auto"
            size="xs"
            variant="outline"
            leftIcon={<ChevronLeftIcon w={4} h={4} />}
            pl={2}
            colorScheme="gray"
            fontFamily="Hero-New"
            onClick={() => setOpenEncounter(null)}>
            Back to Flow Sheet
          </ActionButton>
        ) : (
          <>
            <ButtonGroup size="xs" isAttached variant="outline" ml="auto">
              <Button
                colorScheme={view === 'grid' ? 'blue' : 'gray'}
                fontFamily="Hero-New"
                borderRightWidth={view === 'grid' ? '1px' : 0}
                onClick={() => setView('grid')}>
                Grid
              </Button>
              <Button
                colorScheme={view === 'timeline' ? 'blue' : 'gray'}
                borderLeftWidth={view === 'timeline' ? '1px' : 0}
                fontFamily="Hero-New"
                onClick={() => setView('timeline')}>
                Timeline
              </Button>
            </ButtonGroup>
            <ActionButton size="xs" colorScheme="gray" fontFamily="Hero-New" onClick={openPdf}>
              Export / Print
            </ActionButton>
          </>
        )}
        {onClose && <CloseButton size="sm" onClick={onClose} ml={openEncounter ? 2 : 0} />}
      </Flex>

      {/* ── Body ── */}
      {openEncounter && patientId ? (
        <Box flex={1} overflow="hidden">
          {surface === 'view' && openEncounter.examId ? (
            <EncounterView
              patientId={patientId}
              examId={openEncounter.examId}
              onClose={() => setOpenEncounter(null)}
              onEdit={onEdit}
            />
          ) : (
            <EncounterForm
              patientId={patientId}
              openEncounter={openEncounter}
              assessmentId={assessmentId}
              amendmentReason={amendmentReason}
              onAmendmentReasonConsumed={onAmendmentReasonConsumed}
              onClose={() => setOpenEncounter(null)}
              onSuccess={onSaveSuccess}
            />
          )}
        </Box>
      ) : (
        <Box flex={1} overflowY="auto" pb={20} w="100%">
          {loading ? (
            <Flex justify="center" align="center" h="100%" py={12}>
              <Spinner color="blue.400" />
            </Flex>
          ) : (
            <>
              {/* Layer 1 — Last Visit card */}
              {encounters.length > 0 && (
                <Box
                  bg="green.100"
                  borderBottomWidth="1px"
                  _dark={{ bg: 'green.800', borderColor: 'green.600' }}
                  p={4}
                  w="100%"
                  borderColor="green.300">
                  <LastVisitCard encounter={encounters[0]} onOpenEncounter={handleOpenEncounter} />
                </Box>
              )}

              {/* Layer 2 — Grid or Timeline */}
              <Box>
                {view === 'grid' ? (
                  <FlowSheetGrid
                    encounters={encounters}
                    onOpenEncounter={handleOpenEncounter}
                    onOpenLabQuickview={onOpenLabQuickview}
                    showMore={showMore}
                    onToggleShowMore={() => setShowMore(!showMore)}
                  />
                ) : (
                  <FlowSheetTimeline
                    encounters={timelineEncounters}
                    contacts={contacts}
                    ultrasoundEncounters={ultrasoundEncounters}
                    flags={flags}
                    portalMessages={portalMessages}
                    patientStatements={patientStatements ?? []}
                    onOpenEncounter={handleOpenEncounter}
                    onEditContact={handleEditContact}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Layer 3 — Floating Quick Note button (hidden when an encounter is open) */}
      {!openEncounter && (
        <Button
          position="absolute"
          bottom={4}
          right={4}
          colorScheme="blue"
          fontFamily="Hero-New"
          size="sm"
          fontWeight={700}
          boxShadow="lg"
          onClick={openQuickNote}>
          + Quick Note
        </Button>
      )}

      {patientId && (
        <>
          <QuickNoteModal
            assessmentId={assessmentId}
            patientId={patientId}
            isOpen={isQuickNoteOpen}
            onClose={closeQuickNote}
          />
          <PdfExportModal
            encounters={encounters}
            assessmentId={assessmentId}
            isOpen={isPdfOpen}
            onClose={closePdf}
          />
          {editingContact && (
            <LogContactModal
              assessmentId={assessmentId}
              patientId={patientId}
              isOpen={!!editingContact}
              onClose={handleCloseEditContact}
              editContact={editingContact}
            />
          )}
        </>
      )}
    </Box>
  )
}

interface FlowSheetModalProps extends Props {
  isOpen: boolean
  onClose: () => void
}

export const PrenatalFlowSheetModal = ({
  isOpen,
  onClose,
  defaultView = 'grid',
  ...props
}: FlowSheetModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
    <ModalOverlay />
    <ModalContent borderRadius={0} h="100dvh" display="flex" flexDir="column">
      <ModalBody p={0} flex={1} overflow="hidden">
        <PrenatalFlowSheet {...props} defaultView={defaultView} onClose={onClose} />
      </ModalBody>
    </ModalContent>
  </Modal>
)
